import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight } from "lucide-react";

import { type AuditEntry, type AuditFilter, adminApi } from "@/api/admin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

const PAGE_SIZE = 100;

function fmt(s: string): string {
  return new Date(s).toLocaleString("uk-UA", { timeZone: "Europe/Kyiv" });
}

export function AuditPage() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<AuditFilter>({ limit: PAGE_SIZE, offset: 0 });
  const [expanded, setExpanded] = useState<number | null>(null);

  const audit = useQuery({
    queryKey: ["audit", filter],
    queryFn: () => adminApi.listAudit(filter),
  });

  const entries = useMemo(() => audit.data ?? [], [audit.data]);
  const offset = filter.offset ?? 0;
  const hasMore = entries.length === PAGE_SIZE;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t("audit.title")}</CardTitle>
          <CardDescription>{t("audit.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="space-y-1">
            <Label>{t("audit.entity_type")}</Label>
            <Input
              value={filter.entity_type ?? ""}
              placeholder="client, source, plan, ..."
              onChange={(e) =>
                setFilter((f) => ({ ...f, entity_type: e.target.value || undefined, offset: 0 }))
              }
            />
          </div>
          <div className="space-y-1">
            <Label>{t("audit.action")}</Label>
            <Input
              value={filter.action ?? ""}
              placeholder="create, update, delete, ..."
              onChange={(e) =>
                setFilter((f) => ({ ...f, action: e.target.value || undefined, offset: 0 }))
              }
            />
          </div>
          <div className="space-y-1">
            <Label>{t("audit.from")}</Label>
            <Input
              type="date"
              value={filter.from_date ?? ""}
              onChange={(e) =>
                setFilter((f) => ({ ...f, from_date: e.target.value || undefined, offset: 0 }))
              }
            />
          </div>
          <div className="space-y-1">
            <Label>{t("audit.to")}</Label>
            <Input
              type="date"
              value={filter.to_date ?? ""}
              onChange={(e) =>
                setFilter((f) => ({ ...f, to_date: e.target.value || undefined, offset: 0 }))
              }
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {audit.isLoading ? (
            <div className="p-6 text-sm text-slate-500">{t("common.loading")}</div>
          ) : entries.length === 0 ? (
            <div className="p-6 text-sm text-slate-500">{t("audit.no_entries")}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-slate-500">
                    <th className="p-2 w-8" />
                    <th className="p-2">{t("audit.ts")}</th>
                    <th className="p-2">{t("audit.user")}</th>
                    <th className="p-2">{t("audit.action")}</th>
                    <th className="p-2">{t("audit.entity_type")}</th>
                    <th className="p-2">{t("audit.entity_id")}</th>
                    <th className="p-2">{t("audit.ip")}</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => {
                    const isOpen = expanded === e.id;
                    return (
                      <AuditRow
                        key={e.id}
                        e={e}
                        isOpen={isOpen}
                        onToggle={() => setExpanded(isOpen ? null : e.id)}
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-2">
        <Button
          variant="outline"
          disabled={offset === 0}
          onClick={() =>
            setFilter((f) => ({ ...f, offset: Math.max(0, (f.offset ?? 0) - PAGE_SIZE) }))
          }
        >
          ← {t("audit.prev")}
        </Button>
        <span className="text-xs text-slate-500">{offset + 1}–{offset + entries.length}</span>
        <Button
          variant="outline"
          disabled={!hasMore}
          onClick={() => setFilter((f) => ({ ...f, offset: (f.offset ?? 0) + PAGE_SIZE }))}
        >
          {t("audit.next")} →
        </Button>
      </div>
    </div>
  );
}

function AuditRow({
  e,
  isOpen,
  onToggle,
}: {
  e: AuditEntry;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const hasDetails = e.old_value || e.new_value;
  return (
    <>
      <tr
        onClick={hasDetails ? onToggle : undefined}
        className={hasDetails ? "border-t border-slate-100 cursor-pointer hover:bg-slate-50" : "border-t border-slate-100"}
      >
        <td className="p-2">
          {hasDetails ? (
            isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />
          ) : null}
        </td>
        <td className="p-2 text-xs text-slate-500 whitespace-nowrap">{fmt(e.ts)}</td>
        <td className="p-2 text-xs">{e.user_email ?? "—"}</td>
        <td className="p-2 font-mono text-xs">{e.action}</td>
        <td className="p-2 font-mono text-xs">{e.entity_type}</td>
        <td className="p-2 font-mono text-xs text-slate-500">{e.entity_id}</td>
        <td className="p-2 font-mono text-xs text-slate-500">{e.ip_address ?? "—"}</td>
      </tr>
      {isOpen && hasDetails && (
        <tr className="bg-slate-50/40">
          <td colSpan={7} className="p-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
              {e.old_value && (
                <div>
                  <div className="text-slate-500 mb-1">old_value</div>
                  <pre className="bg-white border border-slate-200 rounded p-2 overflow-x-auto">
                    {JSON.stringify(e.old_value, null, 2)}
                  </pre>
                </div>
              )}
              {e.new_value && (
                <div>
                  <div className="text-slate-500 mb-1">new_value</div>
                  <pre className="bg-white border border-slate-200 rounded p-2 overflow-x-auto">
                    {JSON.stringify(e.new_value, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
